import type { RegisterPushDeviceBody } from '@nucleus-commerce/api-client/admin';

export const PUSH_WORKER_URL = '/push-sw.js';
// No admin page lives under /push/, so the worker controls nothing and coexists with MSW's.
export const PUSH_WORKER_SCOPE = '/push/';
export const PUSH_NAVIGATE_MESSAGE = 'nucleus:push-navigate';

/**
 * supported: this browser can subscribe now. needs-install: iPhone and iPad Safari, which only
 * offer push to a web app added to the Home Screen. unsupported: anything else without push.
 */
export type PushSupport = 'supported' | 'needs-install' | 'unsupported';

export function isStandalone(): boolean {
  const standaloneNavigator = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    standaloneNavigator.standalone === true
  );
}

export function isAppleMobile(userAgent: string): boolean {
  return /iPhone|iPad|iPod/.test(userAgent);
}

export function pushSupport(): PushSupport {
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) {
    return 'supported';
  }
  return isAppleMobile(navigator.userAgent) && !isStandalone() ? 'needs-install' : 'unsupported';
}

export function notificationPermission(): NotificationPermission {
  return 'Notification' in window ? Notification.permission : 'denied';
}

export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

/** Whether the subscription was made with this VAPID key (the server's key can change). */
export function subscribedWith(subscription: PushSubscription, publicKey: string): boolean {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const expected = urlBase64ToUint8Array(publicKey);
  const actual = new Uint8Array(current);
  return (
    actual.length === expected.length && actual.every((byte, index) => byte === expected[index])
  );
}

export function toDeviceBody(subscription: PushSubscription): RegisterPushDeviceBody {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint ?? subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    userAgent: navigator.userAgent.slice(0, 300),
  };
}

function activated(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  if (registration.active) return Promise.resolve(registration);
  const worker = registration.installing ?? registration.waiting;
  if (!worker) return Promise.resolve(registration);
  return new Promise((resolve) => {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') resolve(registration);
    });
  });
}

async function pushRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(PUSH_WORKER_SCOPE);
  const registration =
    existing ??
    (await navigator.serviceWorker.register(PUSH_WORKER_URL, { scope: PUSH_WORKER_SCOPE }));
  return activated(registration);
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'supported') return null;
  const registration = await navigator.serviceWorker.getRegistration(PUSH_WORKER_SCOPE);
  return registration ? registration.pushManager.getSubscription() : null;
}

export class PushPermissionError extends Error {
  constructor(readonly permission: NotificationPermission) {
    super(`Notification permission ${permission}`);
  }
}

/** Asks for permission when needed, then subscribes with the server's key (replacing an old one). */
export async function subscribe(publicKey: string): Promise<PushSubscription> {
  const permission =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;
  if (permission !== 'granted') throw new PushPermissionError(permission);
  const registration = await pushRegistration();
  const existing = await registration.pushManager.getSubscription();
  if (existing && subscribedWith(existing, publicKey)) return existing;
  if (existing) await existing.unsubscribe();
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
}
